import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from './ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Mail, Copy, Eye, Plus, Trash2, Edit, Users, Search, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { API_URL } from '../apiBase';
import { cn } from '@/lib/utils';

const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getPhotoUrl = (photo_url) => {
  if (!photo_url) return null;
  if (photo_url.startsWith('/api/')) {
    return `${API_URL}${photo_url}`;
  }
  return photo_url;
};

/** Aligné backend : email principal/pro ou secondaire, non vide et contenant @. */
const usableEmail = (s) => {
  const t = (s || '').trim();
  return t.length >= 3 && t.includes('@') ? t : '';
};

const hasMailingListEmail = (user) => {
  if (!user) return false;
  if (user.is_external) return Boolean(usableEmail(user.email_external));
  const principal = usableEmail(user.email_principal) || usableEmail(user.email);
  const secondary = usableEmail(user.email_secondaire);
  return Boolean(principal || secondary);
};

const isExternalMember = (member) => Boolean(member?.is_external);

const getUserId = (user) => String(user?.id || user?._id || user?.user_id || '').trim();

const getMemberKey = (member) => {
  if (isExternalMember(member)) return member.id;
  return getUserId(member);
};

const memberEmail = (member) => {
  if (isExternalMember(member)) return usableEmail(member.email_external);
  return (
    usableEmail(member.email_secondaire) ||
    usableEmail(member.email_principal) ||
    usableEmail(member.email)
  );
};

const memberToApiPayload = (member) => {
  if (isExternalMember(member)) {
    const mail = usableEmail(member.email_external);
    return {
      user_id: null,
      email: mail,
      email_secondaire: mail,
      name: mail,
      is_external: true,
    };
  }
  const mail =
    usableEmail(member.email_secondaire) ||
    usableEmail(member.email_principal) ||
    usableEmail(member.email);
  const userId = getUserId(member);
  if (!userId) {
    return null;
  }
  return {
    user_id: userId,
    email: mail,
    email_secondaire: mail,
    name: `${member.prenom || ''} ${member.nom || ''}`.trim() || mail,
    is_external: false,
  };
};

/** Libellé comme à l’écran : « Prénom Nom » ou e-mail pour un membre externe. */
const userDisplayLabel = (u) => {
  if (isExternalMember(u)) return u.email_external || u.nom || '';
  return `${u.prenom || ''} ${u.nom || ''}`.trim();
};

/** Tri alphabétique du libellé affiché (aligné sur les panneaux membres des listes). */
const compareUsersByDisplayLabelFr = (a, b) =>
  userDisplayLabel(a).localeCompare(userDisplayLabel(b), 'fr', { sensitivity: 'base' });

/** Tri du libellé affiché dans les panneaux « membres » (listes auto / statiques). */
const compareMemberDisplayFr = (a, b) =>
  (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' });

const MailingLists = () => {
  const [lists, setLists] = useState([]);
  const [globalLists, setGlobalLists] = useState([]);
  const [personalLists, setPersonalLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('global');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [editingList, setEditingList] = useState(null); // Track which list is being edited
  
  // Create/Edit modal state
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [newListAlias, setNewListAlias] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [externalEmailInput, setExternalEmailInput] = useState('');
  
  // Dynamic list options (for predefined lists only)
  const [listType, setListType] = useState('static'); // 'static' or 'dynamic'
  const [dynamicRules, setDynamicRules] = useState([
    {
      field: '', // 'societe', 'secteur', 'is_admin', etc.
      operator: 'equals', // 'equals', 'contains', 'starts_with', 'not_contains'
      value: '',
      logic: 'AND' // Used only for rules after the first one
    }
  ]);

  // Get permissions
  const { canCreatePredefinedLists } = usePermissions();
  const suppressSheetCloseRef = useRef(false);

  const closeAddMembersModal = useCallback(() => {
    suppressSheetCloseRef.current = true;
    setShowAddMembersModal(false);
    setSearchTerm('');
    setExternalEmailInput('');
    window.setTimeout(() => {
      suppressSheetCloseRef.current = false;
    }, 300);
  }, []);

  useEffect(() => {
    fetchLists();
    fetchUsers();
  }, []);

  useEffect(() => {
    // Separate global and personal lists
    const global = lists.filter(list => list.type === 'global');
    const personal = lists.filter(list => list.type === 'personal');
    console.log('📊 Filtering lists - Global:', global.length, 'Personal:', personal.length);
    console.log('📊 Global lists details:', global.map(l => ({
      name: l.name,
      total_members: l.total_members,
      is_dynamic: l.is_dynamic
    })));
    setGlobalLists(global);
    setPersonalLists(personal);
  }, [lists, refreshTrigger]);

  const fetchLists = async () => {
    try {
      setLoading(true);
      // Add cache busting and detailed logging
      const response = await axios.get(`${API_URL}/api/mailing-lists`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        params: {
          _t: new Date().getTime() // Cache buster
        }
      });
      console.log('📋 Lists fetched from API (RAW):', JSON.stringify(response.data, null, 2));
      // Log dynamic lists specifically
      const dynamicLists = response.data.filter(l => l.is_dynamic);
      if (dynamicLists.length > 0) {
        console.log('🔄 Dynamic lists:', dynamicLists.map(l => ({
          name: l.name,
          total_members: l.total_members,
          members_count: l.members?.length,
          dynamic_rules: l.dynamic_rules
        })));
      }
      // Force new array reference to trigger React re-render
      setLists([...response.data]);
    } catch (error) {
      console.error('Error fetching lists:', error);
      toast.error('Erreur lors du chargement des listes');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setAvailableUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const copyEmails = async (list) => {
    try {
      // Build email string (separated by semicolons for Outlook)
      // Utilise email_secondaire comme champ principal
      const emails = [...(list.members || [])]
        .sort(compareMemberDisplayFr)
        .map((m) => usableEmail(m.email_secondaire) || usableEmail(m.email))
        .filter(Boolean)
        .join('; ');
      
      if (!emails) {
        toast.error('Aucun email à copier');
        return;
      }
      
      // Copy to clipboard
      await navigator.clipboard.writeText(emails);
      
      // Track the copy
      await axios.post(`${API_URL}/api/mailing-lists/${list.id}/copy`);
      
      // Show success message
      const emailCount = emails.split('; ').length;
      toast.success(`${emailCount} email(s) copié(s) dans le presse-papier !`, {
        description: 'Vous pouvez maintenant les coller dans Outlook',
        icon: <CheckCircle2 className="h-5 w-5 text-green-500" />
      });
    } catch (error) {
      console.error('Error copying emails:', error);
      toast.error('Erreur lors de la copie des emails');
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) {
      toast.error('Veuillez saisir un nom pour la liste');
      return;
    }

    // Validation for static lists
    if (listType === 'static' && selectedMembers.length === 0) {
      toast.error('Veuillez ajouter au moins un membre');
      return;
    }

    if (listType === 'static' && selectedMembers.some((m) => !hasMailingListEmail(m))) {
      toast.error('Chaque membre doit avoir une adresse e-mail valide.');
      return;
    }
    if (listType === 'static') {
      const invalidInternal = selectedMembers.filter(
        (m) => !isExternalMember(m) && !getUserId(m)
      );
      if (invalidInternal.length > 0) {
        toast.error('Certains membres de l\'annuaire sont invalides. Veuillez les resélectionner.');
        return;
      }
    }
    if (listType === 'dynamic') {
      const validRules = dynamicRules.filter(rule => rule.field && rule.value);
      if (validRules.length === 0) {
        toast.error('Veuillez configurer au moins un critère complet (champ et valeur requis)');
        return;
      }
    }

    try {
      // Determine list type based on active tab and user permissions
      const globalListType = (activeTab === 'global' && canCreatePredefinedLists) ? 'global' : 'personal';
      const memberPayloads = listType === 'static'
        ? selectedMembers.map(memberToApiPayload).filter(Boolean)
        : [];

      if (listType === 'static' && memberPayloads.length !== selectedMembers.length) {
        toast.error('Certains membres sélectionnés sont invalides. Veuillez les resélectionner.');
        return;
      }
      
      const newList = {
        name: newListName,
        description: newListDescription,
        type: globalListType,
        is_dynamic: listType === 'dynamic',
        members: memberPayloads,
        dynamic_rules: listType === 'dynamic' ? convertRulesToAPIFormat(dynamicRules) : null,
        ...(globalListType === 'global'
          ? { email_alias: newListAlias.trim() || null }
          : {}),
      };

      console.log('📤 Creating list:', newList);
      const createResponse = await axios.post(`${API_URL}/api/mailing-lists`, newList);
      console.log('✅ List created:', createResponse.data);
      
      if (globalListType === 'global') {
        const typeLabel = listType === 'dynamic' ? 'dynamique' : 'statique';
        toast.success(`Liste prédéfinie ${typeLabel} créée avec succès ! Elle est maintenant accessible par tous les utilisateurs.`);
      } else {
        toast.success('Liste personnelle créée avec succès !');
      }
      
      setShowCreateModal(false);
      resetCreateForm();
      
      // Add a small delay before fetching to ensure backend has processed
      console.log('⏳ Waiting before fetch...');
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('🔄 Fetching lists now...');
      await fetchLists();
      
      // Force React to re-render by triggering the useEffect
      console.log('🔄 Triggering state refresh...');
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error creating list:', error);
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Erreur lors de la création de la liste');
    }
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette liste ?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/mailing-lists/${listId}`);
      toast.success('Liste supprimée avec succès');
      fetchLists();
    } catch (error) {
      console.error('Error deleting list:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEditList = (list) => {
    console.log('📝 Editing list:', list);
    setEditingList(list);
    setNewListName(list.name);
    setNewListDescription(list.description || '');
    setNewListAlias(list.email_alias || '');
    
    // Set list type (static or dynamic)
    setListType(list.is_dynamic ? 'dynamic' : 'static');
    
    // If dynamic, load the rules
    if (list.is_dynamic && list.dynamic_rules) {
      // Handle both old format (array of rules) and new format (object with logic + rules)
      if (list.dynamic_rules.logic && list.dynamic_rules.rules) {
        // New format with global logic - convert to per-rule logic
        const globalLogic = list.dynamic_rules.logic;
        const convertedRules = list.dynamic_rules.rules.map((rule, index) => ({
          ...rule,
          logic: index === 0 ? 'AND' : (rule.logic || globalLogic) // First rule doesn't need logic, others inherit
        }));
        setDynamicRules(convertedRules);
      } else if (Array.isArray(list.dynamic_rules) && list.dynamic_rules.length > 0) {
        // Old format - array of rules with optional per-rule logic
        const rulesWithLogic = list.dynamic_rules.map((rule, index) => ({
          ...rule,
          logic: rule.logic || 'AND' // Default to AND if not specified
        }));
        setDynamicRules(rulesWithLogic);
      } else {
        // Fallback - single rule as object (very old format)
        setDynamicRules([{ ...list.dynamic_rules, logic: 'AND' }]);
      }
    }
    
    // If static, load members (objets utilisateur complets, uniquement avec email pro principal)
    if (!list.is_dynamic && list.members) {
      const resolved = list.members
        .map((member) => {
          if (member.is_external || !member.user_id) {
            const mail = usableEmail(member.email_secondaire) || usableEmail(member.email);
            if (!mail) return null;
            return {
              id: `external:${mail.toLowerCase()}`,
              is_external: true,
              email_external: mail,
              prenom: '',
              nom: member.name || mail,
            };
          }
          return availableUsers.find((u) => getUserId(u) === String(member.user_id));
        })
        .filter((m) => m && hasMailingListEmail(m));
      setSelectedMembers(resolved);
    }
    
    setShowCreateModal(true);
  };

  const handleUpdateList = async () => {
    if (!newListName.trim()) {
      toast.error('Veuillez entrer un nom pour la liste');
      return;
    }

    // Validation for static lists
    if (listType === 'static' && selectedMembers.length === 0) {
      toast.error('Veuillez ajouter au moins un membre');
      return;
    }

    if (listType === 'static' && selectedMembers.some((m) => !hasMailingListEmail(m))) {
      toast.error('Chaque membre doit avoir une adresse e-mail valide.');
      return;
    }
    if (listType === 'static') {
      const invalidInternal = selectedMembers.filter(
        (m) => !isExternalMember(m) && !getUserId(m)
      );
      if (invalidInternal.length > 0) {
        toast.error('Certains membres de l\'annuaire sont invalides. Veuillez les resélectionner.');
        return;
      }
    }
    
    // Validation for dynamic lists - filter out incomplete rules
    if (listType === 'dynamic') {
      const validRules = dynamicRules.filter(rule => rule.field && rule.value);
      if (validRules.length === 0) {
        toast.error('Veuillez configurer au moins un critère complet (champ et valeur requis)');
        return;
      }
    }

    try {
      const memberPayloads = listType === 'static'
        ? selectedMembers.map(memberToApiPayload).filter(Boolean)
        : null;

      if (listType === 'static' && memberPayloads.length !== selectedMembers.length) {
        toast.error('Certains membres sélectionnés sont invalides. Veuillez les resélectionner.');
        return;
      }

      const updateData = {
        name: newListName,
        description: newListDescription,
        members: memberPayloads,
        dynamic_rules: listType === 'dynamic' ? convertRulesToAPIFormat(dynamicRules) : null,
        ...(editingList?.type === 'global'
          ? { email_alias: newListAlias.trim() }
          : {}),
      };

      console.log('📝 Updating list:', editingList.id, updateData);
      await axios.put(`${API_URL}/api/mailing-lists/${editingList.id}`, updateData);
      
      toast.success('Liste modifiée avec succès !');
      
      setShowCreateModal(false);
      setEditingList(null);
      resetCreateForm();
      
      // Refresh lists
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchLists();
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error updating list:', error);
      const msg = error.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Erreur lors de la modification de la liste');
    }
  };

  const resetCreateForm = () => {
    setNewListName('');
    setNewListDescription('');
    setNewListAlias('');
    setSelectedMembers([]);
    setSearchTerm('');
    setExternalEmailInput('');
    setListType('static');
    setDynamicRules([{
      field: '',
      operator: 'equals',
      value: '',
      logic: 'AND'
    }]);
    setEditingList(null); // Reset editing state
  };

  // Helper to convert per-rule logic to global logic format for API
  const convertRulesToAPIFormat = (rules) => {
    // Filter only complete rules (field + value)
    const validRules = rules.filter(r => r.field && r.value);
    if (validRules.length === 0) return null;
    
    // Keep the logic field in each rule for backend processing
    // The backend needs per-rule logic to build sequential queries
    if (validRules.length === 1) {
      return {
        logic: 'AND',
        rules: validRules // Keep logic field
      };
    }
    
    // Determine global logic based on most common operator (for compatibility)
    const logics = validRules.slice(1).map(r => r.logic || 'AND');
    const globalLogic = logics.every(l => l === logics[0]) ? logics[0] : 'AND';
    
    return {
      logic: globalLogic,
      rules: validRules // Keep logic field in each rule
    };
  };

  // Functions for managing dynamic rules
  const addDynamicRule = () => {
    setDynamicRules([...dynamicRules, { field: '', operator: 'equals', value: '', logic: 'AND' }]);
  };

  const removeDynamicRule = (index) => {
    if (dynamicRules.length > 1) {
      setDynamicRules(dynamicRules.filter((_, i) => i !== index));
    }
  };

  const updateDynamicRule = (index, field, value) => {
    const newRules = [...dynamicRules];
    newRules[index] = { ...newRules[index], [field]: value };
    setDynamicRules(newRules);
  };

  const addMember = (user) => {
    if (!hasMailingListEmail(user)) return;
    const userId = getUserId(user);
    if (!userId) return;
    if (selectedMembers.find((m) => getMemberKey(m) === userId)) return;
    setSelectedMembers([...selectedMembers, { ...user, id: userId }]);
  };

  const removeMember = (memberKey) => {
    setSelectedMembers(selectedMembers.filter((m) => getMemberKey(m) !== memberKey));
  };

  const addExternalMember = () => {
    const email = usableEmail(externalEmailInput.trim().toLowerCase());
    if (!email) {
      toast.error('Veuillez saisir une adresse e-mail valide');
      return;
    }
    const id = `external:${email}`;
    if (selectedMembers.find((m) => m.id === id)) {
      toast.error('Cette adresse e-mail est déjà dans la liste');
      return;
    }
    const duplicateInternal = selectedMembers.some(
      (m) => !isExternalMember(m) && memberEmail(m)?.toLowerCase() === email
    );
    if (duplicateInternal) {
      toast.error('Cet e-mail correspond déjà à un membre de l\'annuaire sélectionné');
      return;
    }
    setSelectedMembers([
      ...selectedMembers,
      {
        id,
        is_external: true,
        email_external: email,
        prenom: '',
        nom: email,
      },
    ]);
    setExternalEmailInput('');
  };

  const usersEligibleForStaticList = [...availableUsers]
    .filter(hasMailingListEmail)
    .sort(compareUsersByDisplayLabelFr);
  const filteredUsers = usersEligibleForStaticList
    .filter((user) => {
      const searchLower = searchTerm.toLowerCase();
      const fullName = `${user.prenom} ${user.nom}`.toLowerCase();
      const emailHaystack = [user.email_principal, user.email, user.email_secondaire]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return fullName.includes(searchLower) || emailHaystack.includes(searchLower);
    })
    .sort(compareUsersByDisplayLabelFr);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Chargement des listes...</div>
      </div>
    );
  }

  const displayedLists = activeTab === 'global' ? globalLists : personalLists;
  const listSidePanelOpen = showCreateModal || showMembersModal;
  const allowExternalMembers = activeTab === 'personal' || editingList?.type === 'personal';

  return (
    <div
      className={cn(
        'space-y-6',
        listSidePanelOpen &&
          'transition-[margin] duration-500 ease-out sm:mr-[32rem] md:mr-[36rem] lg:mr-[42rem]'
      )}
    >
      {/* Tabs */}
      <div className="flex items-center gap-4">
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'global' ? 'default' : 'outline'}
            onClick={() => setActiveTab('global')}
          >
            Listes Prédéfinies
          </Button>
          <Button
            variant={activeTab === 'personal' ? 'default' : 'outline'}
            onClick={() => setActiveTab('personal')}
          >
            Mes Listes
          </Button>
        </div>
        
        {/* Show "Create" button only for predefined lists if user has permission */}
        {activeTab === 'global' && canCreatePredefinedLists && (
          <Button onClick={() => setShowCreateModal(true)} className="ml-auto">
            <Plus className="h-4 w-4 mr-2" />
            Créer une liste
          </Button>
        )}
        
        {/* Show "Create" button for personal lists (always allowed) */}
        {activeTab === 'personal' && (
          <Button onClick={() => setShowCreateModal(true)} className="ml-auto">
            <Plus className="h-4 w-4 mr-2" />
            Créer une liste
          </Button>
        )}
      </div>

      {/* Lists Display */}
      <div className="space-y-4">
        {displayedLists.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {activeTab === 'global' 
              ? 'Aucune liste prédéfinie disponible'
              : 'Vous n\'avez pas encore créé de liste personnelle'}
          </div>
        ) : (
          displayedLists.map(list => (
            <div
              key={list.id}
              className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-card"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Mail className="h-5 w-5 text-primary shrink-0" />
                    <h3 className="font-semibold text-lg">{list.name}</h3>
                    {list.is_dynamic && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-500/10 text-green-500 text-xs font-medium">
                        🔄 Automatique
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Users className="h-4 w-4 shrink-0" />
                      {list.total_members} membre{list.total_members > 1 ? 's' : ''}
                    </span>
                  </div>
                  {list.description && (
                    <p className="text-sm text-muted-foreground">{list.description}</p>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => copyEmails(list)}
                    className="gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copier emails
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedList(list);
                      setShowMembersModal(true);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  {(list.type === 'personal' || canCreatePredefinedLists) && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditList(list)}
                        title="Modifier la liste"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteList(list.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Création / édition — panneau latéral droit */}
      <Sheet
        open={showCreateModal}
        modal={!showAddMembersModal}
        onOpenChange={(open) => {
          if (!open && (showAddMembersModal || suppressSheetCloseRef.current)) {
            return;
          }
          setShowCreateModal(open);
          if (!open) {
            setShowAddMembersModal(false);
            resetCreateForm();
          }
        }}
      >
        <SheetContent
          side="right"
          overlayClassName="bg-black/50"
          className="w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl p-0 flex flex-col gap-0 border-l"
        >
          <SheetHeader className="px-6 pt-6 pb-4 pr-14 text-left border-b border-border shrink-0 space-y-1.5">
            <SheetTitle>
              {editingList
                ? 'Modifier la Liste'
                : (activeTab === 'global' ? 'Créer une Liste Prédéfinie' : 'Créer une Liste Personnelle')}
            </SheetTitle>
            <SheetDescription>
              {editingList
                ? 'Modifiez les informations de votre liste'
                : (activeTab === 'global'
                    ? 'Créez une liste prédéfinie accessible à tous les utilisateurs'
                    : 'Créez une liste personnelle pour envoyer des emails groupés rapidement')}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Nom de la liste *</label>
              <Input
                placeholder="Ex: Mes Contacts Urgents"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Description (optionnel)</label>
              <Input
                placeholder="Description de la liste"
                value={newListDescription}
                onChange={(e) => setNewListDescription(e.target.value)}
              />
            </div>

            {(activeTab === 'global' || editingList?.type === 'global') && (
              <div>
                <label className="text-sm font-medium mb-2 block">Alias e-mail (optionnel)</label>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="ex. ma-conciergerie"
                    value={newListAlias}
                    onChange={(e) => setNewListAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    className="flex-1"
                  />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">@domaine</span>
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Adresse courte pour retrouver la liste dans Outlook (champ À). Appliquée à Exchange
                  lors de la prochaine synchronisation des listes.
                </p>
              </div>
            )}

            {/* Show dynamic/static choice only for global lists and only when creating (not editing) */}
            {activeTab === 'global' && canCreatePredefinedLists && !editingList && (
              <div className="border-t pt-4">
                <label className="text-sm font-medium mb-3 block">Type de liste</label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="static"
                      name="listType"
                      value="static"
                      checked={listType === 'static'}
                      onChange={(e) => setListType(e.target.value)}
                      className="h-4 w-4"
                    />
                    <label htmlFor="static" className="text-sm">
                      <span className="font-medium">Liste Statique</span> - Sélection manuelle des membres
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="dynamic"
                      name="listType"
                      value="dynamic"
                      checked={listType === 'dynamic'}
                      onChange={(e) => setListType(e.target.value)}
                      className="h-4 w-4"
                    />
                    <label htmlFor="dynamic" className="text-sm">
                      <span className="font-medium">Liste Dynamique</span> - Mise à jour automatique selon des critères
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Dynamic rules configuration */}
            {listType === 'dynamic' && (
              <div className="border-t pt-4 space-y-4">
                <label className="text-sm font-medium block">Configuration automatique</label>

                {/* Dynamic Rules List */}
                <div className="space-y-3">
                  {dynamicRules.map((rule, index) => (
                    <div key={index}>
                      {/* Logic operator selector for rules after the first one */}
                      {index > 0 && (
                        <div className="flex items-center gap-3 mb-2 ml-4">
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              id={`logic-and-${index}`}
                              name={`logic-${index}`}
                              value="AND"
                              checked={rule.logic === 'AND'}
                              onChange={() => updateDynamicRule(index, 'logic', 'AND')}
                              className="h-4 w-4"
                            />
                            <label htmlFor={`logic-and-${index}`} className="text-xs cursor-pointer">
                              <span className="font-medium">ET</span>
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="radio"
                              id={`logic-or-${index}`}
                              name={`logic-${index}`}
                              value="OR"
                              checked={rule.logic === 'OR'}
                              onChange={() => updateDynamicRule(index, 'logic', 'OR')}
                              className="h-4 w-4"
                            />
                            <label htmlFor={`logic-or-${index}`} className="text-xs cursor-pointer">
                              <span className="font-medium">OU</span>
                            </label>
                          </div>
                        </div>
                      )}
                      
                      <div className="border rounded-lg p-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground">Critère {index + 1}</span>
                          {dynamicRules.length > 1 && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => removeDynamicRule(index)}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Champ</label>
                          <Select 
                            value={rule.field} 
                            onValueChange={(value) => updateDynamicRule(index, 'field', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un champ" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="societe">Société</SelectItem>
                              <SelectItem value="secteur">Secteur</SelectItem>
                              <SelectItem value="poste">Poste</SelectItem>
                              <SelectItem value="service">Service</SelectItem>
                              <SelectItem value="nom">Nom</SelectItem>
                              <SelectItem value="prenom">Prénom</SelectItem>
                              <SelectItem value="email">Email</SelectItem>
                              <SelectItem value="ville">Ville</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Opérateur</label>
                          <Select 
                            value={rule.operator} 
                            onValueChange={(value) => updateDynamicRule(index, 'operator', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">Égale à</SelectItem>
                              <SelectItem value="contains">Contient</SelectItem>
                              <SelectItem value="starts_with">Commence par</SelectItem>
                              <SelectItem value="not_contains">Ne contient pas</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Valeur</label>
                          <Input
                            placeholder="Valeur à rechercher"
                            value={rule.value}
                            onChange={(e) => updateDynamicRule(index, 'value', e.target.value)}
                          />
                        </div>
                      </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Add Rule Button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addDynamicRule}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un critère
                  </Button>
                </div>
              </div>
            )}

            {/* Members selection - only for static lists */}
            {listType === 'static' && (
              <div className="border-t pt-4">
                <label className="text-sm font-medium mb-3 block">Membres de la liste</label>
                
                {/* Selected members display */}
                {selectedMembers.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-muted-foreground mb-2">
                      {selectedMembers.length} membre{selectedMembers.length > 1 ? 's' : ''} sélectionné{selectedMembers.length > 1 ? 's' : ''}
                    </div>
                    <div className="space-y-2 max-h-[min(55vh,28rem)] overflow-y-auto border rounded-md p-3 bg-muted/20">
                      {[...selectedMembers].sort(compareUsersByDisplayLabelFr).map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between p-2 bg-background rounded border"
                        >
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              {isExternalMember(member) ? (
                                <AvatarFallback className="text-xs">
                                  <Mail className="h-3.5 w-3.5" />
                                </AvatarFallback>
                              ) : (
                                <>
                                  <AvatarImage src={getPhotoUrl(member.photo_url)} />
                                  <AvatarFallback>{getInitials(`${member.prenom} ${member.nom}`)}</AvatarFallback>
                                </>
                              )}
                            </Avatar>
                            <div>
                              <div className="text-sm font-medium">
                                {isExternalMember(member)
                                  ? member.email_external
                                  : `${member.prenom} ${member.nom}`}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {isExternalMember(member)
                                  ? 'Membre externe'
                                  : memberEmail(member)}
                              </div>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeMember(getMemberKey(member))}
                            className="h-6 w-6 p-0"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add members button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddMembersModal(true)}
                  className="w-full gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {selectedMembers.length > 0 ? "Ajouter d'autres membres" : 'Ajouter des membres'}
                </Button>
              </div>
            )}
          </div>

          <SheetFooter className="px-6 py-4 border-t border-border bg-background shrink-0 flex-row justify-end gap-2 sm:space-x-0">
            <Button
              variant="outline"
              type="button"
              onClick={() => setShowCreateModal(false)}
            >
              Annuler
            </Button>
            <Button type="button" onClick={editingList ? handleUpdateList : handleCreateList}>
              {editingList ? 'Enregistrer les modifications' : 'Créer la liste'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Membres de la liste — panneau latéral droit */}
      <Sheet open={showMembersModal} onOpenChange={setShowMembersModal}>
        <SheetContent
          side="right"
          overlayClassName="bg-black/50"
          className="w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl p-0 flex flex-col gap-0 border-l"
        >
          <SheetHeader className="px-6 pt-6 pb-4 pr-14 text-left border-b border-border shrink-0 space-y-1.5">
            <SheetTitle>{selectedList?.name}</SheetTitle>
            <SheetDescription>
              {selectedList?.total_members} membre{selectedList?.total_members > 1 ? 's' : ''} dans cette liste
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-0">
            {[...(selectedList?.members ?? [])].sort(compareMemberDisplayFr).map((member) => {
              const rowEmail =
                usableEmail(member.email_secondaire) || usableEmail(member.email);
              const isExternal = Boolean(member.is_external || !member.user_id);
              return (
                <div key={member.user_id || rowEmail} className="flex items-center gap-3 p-3 border rounded-lg">
                  <Avatar>
                    <AvatarFallback>
                      {isExternal ? <Mail className="h-4 w-4" /> : getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium flex items-center gap-2">
                      <span className="truncate">{member.name}</span>
                      {isExternal && (
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          Externe
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground truncate" title={rowEmail || undefined}>
                      {rowEmail || '—'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <SheetFooter className="px-6 py-4 border-t border-border bg-background shrink-0 flex-row justify-end gap-2 sm:space-x-0">
            <Button type="button" variant="outline" onClick={() => setShowMembersModal(false)}>
              Fermer
            </Button>
            <Button
              type="button"
              onClick={() => {
                copyEmails(selectedList);
                setShowMembersModal(false);
              }}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copier tous les emails
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Add Members Modal */}
      <Dialog
        open={showAddMembersModal}
        onOpenChange={(open) => {
          if (open) {
            setShowAddMembersModal(true);
          } else {
            closeAddMembersModal();
          }
        }}
      >
        <DialogContent overlayClassName="z-[90]" className="z-[100] max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ajouter des membres</DialogTitle>
            <DialogDescription>
              Sélectionnez les utilisateurs de l&apos;annuaire
              {allowExternalMembers ? ' ou ajoutez une adresse e-mail externe' : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {allowExternalMembers && (
              <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                <div>
                  <p className="text-sm font-medium">Membre externe</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ajoutez une adresse e-mail qui ne figure pas dans l&apos;annuaire interne.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    type="email"
                    placeholder="exemple@domaine.fr"
                    value={externalEmailInput}
                    onChange={(e) => setExternalEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addExternalMember();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button type="button" variant="secondary" onClick={addExternalMember}>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter
                  </Button>
                </div>
              </div>
            )}

            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un utilisateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Users list with checkboxes */}
            <div className="space-y-1 max-h-96 overflow-y-auto border rounded-md p-2">
              {filteredUsers.map(user => {
                const userId = getUserId(user);
                const isSelected = selectedMembers.find((m) => getMemberKey(m) === userId);
                return (
                  <div
                    key={userId || `user-${user.prenom}-${user.nom}`}
                    className={`flex items-center gap-3 p-2 hover:bg-muted rounded cursor-pointer transition-colors ${
                      isSelected ? 'bg-muted border border-primary/20' : ''
                    }`}
                    onClick={() => (isSelected ? removeMember(userId) : addMember(user))}
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={!!isSelected}
                      onChange={() => {}}
                      className="h-4 w-4 rounded border-gray-300 flex-shrink-0"
                    />
                    
                    {/* Avatar */}
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarImage src={getPhotoUrl(user.photo_url)} />
                      <AvatarFallback className="text-xs">{getInitials(`${user.prenom} ${user.nom}`)}</AvatarFallback>
                    </Avatar>
                    
                    {/* User info - simplified layout */}
                    <div className="flex-1 min-w-0 flex items-center justify-between">
                      <div className="font-medium text-sm truncate">
                        {user.prenom} {user.nom}
                      </div>
                      <div className="text-xs text-muted-foreground truncate max-w-60" title={user.email}>
                        {user.email}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {filteredUsers.length === 0 && searchTerm && (
                <div className="text-center py-8 text-muted-foreground">
                  Aucun utilisateur trouvé pour &quot;{searchTerm}&quot;
                </div>
              )}
              
              {filteredUsers.length === 0 && !searchTerm && usersEligibleForStaticList.length > 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Utilisez la barre de recherche pour trouver des utilisateurs
                </div>
              )}

              {filteredUsers.length === 0 && !searchTerm && usersEligibleForStaticList.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Aucun utilisateur disponible avec une adresse email professionnelle principale ou secondaire valide.
                </div>
              )}
            </div>

            {/* Selected count */}
            {selectedMembers.length > 0 && (
              <div className="text-sm text-muted-foreground text-center">
                {selectedMembers.length} membre{selectedMembers.length > 1 ? 's' : ''} sélectionné{selectedMembers.length > 1 ? 's' : ''}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeAddMembersModal}
            >
              Annuler
            </Button>
            <Button type="button" onClick={closeAddMembersModal}>
              Confirmer ({selectedMembers.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MailingLists;
